import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  coverImage,
  saludos,
  scoreAccionesSaludos,
  reglasSaludos,
  globalStatsSaludos,
  tipoLabels,
  estadoLabelsSaludo,
  destinoLabels,
  getMasCompartidos,
  getMayorScore,
  getMasRecientes,
  getSaludosPorEstado,
} from '@/mocks/sistemaSaludos';
import type { EstadoSaludo, TipoSaludo, DestinoSaludo } from '@/mocks/sistemaSaludos';

type Ordenamiento = 'recientes' | 'mayor-score' | 'mas-compartidos' | 'al-aire' | 'destacados';

export default function SistemaSaludosPage() {
  const [ordenamiento, setOrdenamiento] = useState<Ordenamiento>('recientes');
  const [tipoFiltro, setTipoFiltro] = useState<TipoSaludo | 'todos'>('todos');
  const [destinoFiltro, setDestinoFiltro] = useState<DestinoSaludo | 'todos'>('todos');
  const [estadoFiltro, setEstadoFiltro] = useState<EstadoSaludo | 'todos'>('todos');
  const [vista, setVista] = useState<'saludos' | 'reglas'>('saludos');
  const [saludoExpandido, setSaludoExpandido] = useState<number | null>(null);

  const ordenados = (() => {
    let base = [...saludos];
    if (tipoFiltro !== 'todos') base = base.filter((s) => s.tipo === tipoFiltro);
    if (destinoFiltro !== 'todos') base = base.filter((s) => s.destino === destinoFiltro);
    if (estadoFiltro !== 'todos') base = base.filter((s) => s.estado === estadoFiltro);

    switch (ordenamiento) {
      case 'recientes':
        return base.sort((a, b) => new Date(b.fechaEnvio).getTime() - new Date(a.fechaEnvio).getTime());
      case 'mayor-score':
        return base.sort((a, b) => b.score - a.score);
      case 'mas-compartidos':
        return base.sort((a, b) => b.compartidos - a.compartidos);
      case 'al-aire':
        return base.filter((s) => s.estado === 'al_aire');
      case 'destacados':
        return base.filter((s) => s.estado === 'destacado');
      default:
        return base;
    }
  })();

  const tipoPills: { tipo: TipoSaludo | 'todos'; label: string; icono: string }[] = [
    { tipo: 'todos', label: 'Todos', icono: 'ri-filter-line' },
    { tipo: 'texto', label: 'Texto', icono: 'ri-text' },
    { tipo: 'audio', label: 'Audio', icono: 'ri-mic-line' },
    { tipo: 'emoji', label: 'Emoji', icono: 'ri-emotion-line' },
    { tipo: 'imagen', label: 'Imagen', icono: 'ri-image-line' },
  ];

  const destinoPills: { destino: DestinoSaludo | 'todos'; label: string }[] = [
    { destino: 'todos', label: 'Todo' },
    { destino: 'radio', label: 'Radio' },
    { destino: 'programa', label: 'Programa' },
    { destino: 'locutor', label: 'Locutor' },
    { destino: 'evento', label: 'Evento' },
  ];

  const estadoPills: { estado: EstadoSaludo | 'todos'; label: string; dot: string }[] = [
    { estado: 'todos', label: 'Todos', dot: 'bg-foreground-400' },
    { estado: 'recibido', label: 'Recibidos', dot: 'bg-foreground-400' },
    { estado: 'leido', label: 'Leídos', dot: 'bg-primary-500' },
    { estado: 'destacado', label: 'Destacados', dot: 'bg-amber-500' },
    { estado: 'al_aire', label: 'Al Aire', dot: 'bg-emerald-500' },
  ];

  const ordenamientos: { id: Ordenamiento; label: string; icono: string }[] = [
    { id: 'recientes', label: 'Más Recientes', icono: 'ri-time-line' },
    { id: 'mayor-score', label: 'Mayor Score', icono: 'ri-flashlight-line' },
    { id: 'mas-compartidos', label: 'Más Compartidos', icono: 'ri-share-forward-line' },
    { id: 'al-aire', label: 'Al Aire', icono: 'ri-broadcast-line' },
    { id: 'destacados', label: 'Destacados', icono: 'ri-star-line' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero */}
      <div className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
        <img
          src={coverImage}
          alt="Sistema de Saludos"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-500/20 backdrop-blur-sm border border-rose-400/30 text-rose-200 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-400 animate-pulse"></span>
            SISTEMA DE SALUDOS
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
            Saludá, Dedicá y Conectá
          </h1>
          <p className="text-white/70 text-sm md:text-base max-w-[560px] leading-relaxed">
            Enviá saludos a radios, programas, locutores y eventos. Con texto, audio, emoji o imagen. Seguí tu saludo en tiempo real. Todo dentro de Cabina.
          </p>
          <div className="flex items-center gap-3 mt-6">
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsSaludos.saludosHoy} saludos hoy
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsSaludos.saludosAlAire} al aire
            </span>
            <span className="text-white/80 text-[10px] bg-background-50/10 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {globalStatsSaludos.scoreTotalGenerado.toLocaleString()} score
            </span>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4 overflow-x-auto">
            <div className="flex items-center gap-4 shrink-0">
              <Link to="/" className="flex items-center gap-1.5 text-foreground-400 hover:text-foreground-700 transition-colors">
                <i className="ri-arrow-left-line"></i>
                <span className="text-xs font-semibold">RFW</span>
              </Link>
              <div className="flex items-center gap-4">
                <span className="text-foreground-500 text-[10px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500"></span>
                  {globalStatsSaludos.saludosHoy} hoy
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden sm:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  {globalStatsSaludos.saludosAlAire} al aire
                </span>
                <span className="text-foreground-500 text-[10px] flex items-center gap-1 hidden md:inline">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                  {globalStatsSaludos.saludosDestacados} destacados
                </span>
                <span className="text-foreground-400 text-[10px] hidden lg:inline">
                  Top: {globalStatsSaludos.saludoMasCompartido}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setVista('saludos')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'saludos' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-message-3-line mr-1"></i>Saludos
              </button>
              <button
                onClick={() => setVista('reglas')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  vista === 'reglas' ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                <i className="ri-scales-line mr-1"></i>Reglas
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
        {vista === 'reglas' ? (
          /* ---- REGLAS VIEW ---- */
          <div className="max-w-[1000px] mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">Reglas del Sistema de Saludos</h2>
              <p className="text-foreground-500 text-sm">Cómo funciona el sistema de saludos en RFW.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {reglasSaludos.map((regla) => (
                <div key={regla.id} className="bg-background-50 rounded-2xl border border-background-200 p-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${regla.color} flex items-center justify-center shrink-0`}>
                      <i className={`${regla.icono} text-background-50 text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="text-foreground-950 font-bold text-sm mb-2">{regla.titulo}</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">{regla.descripcion}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Score Section */}
            <div className="mb-8">
              <h3 className="text-foreground-950 font-bold text-sm mb-4 text-center">Score por Participar</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {scoreAccionesSaludos.map((acc) => (
                  <div key={acc.id} className="bg-background-50 rounded-2xl border border-background-200 p-5 text-center">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${acc.color} flex items-center justify-center mx-auto mb-3`}>
                      <i className={`${acc.icono} text-background-50 text-lg`}></i>
                    </div>
                    <p className="text-foreground-950 font-bold text-sm mb-1">{acc.accion}</p>
                    <p className="text-foreground-500 text-xs leading-relaxed mb-3">{acc.descripcion}</p>
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 text-xs font-bold">
                      <i className="ri-add-line"></i>+{acc.puntos} pts
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Stats Summary Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Saludos Hoy', valor: globalStatsSaludos.saludosHoy },
                { label: 'Al Aire', valor: globalStatsSaludos.saludosAlAire },
                { label: 'Destacados', valor: globalStatsSaludos.saludosDestacados },
                { label: 'Score Generado', valor: globalStatsSaludos.scoreTotalGenerado.toLocaleString() },
              ].map((s) => (
                <div key={s.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
                  <p className="text-foreground-950 font-bold text-xl">{s.valor}</p>
                  <p className="text-foreground-400 text-[10px]">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Tipos breakdown in reglas */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {[
                { label: 'Textos', valor: globalStatsSaludos.tiposTexto, icono: 'ri-text', bg: 'bg-background-50' },
                { label: 'Audios', valor: globalStatsSaludos.tiposAudio, icono: 'ri-mic-line', bg: 'bg-violet-50' },
                { label: 'Emojis', valor: globalStatsSaludos.tiposEmoji, icono: 'ri-emotion-line', bg: 'bg-amber-50' },
                { label: 'Imágenes', valor: globalStatsSaludos.tiposImagen, icono: 'ri-image-line', bg: 'bg-sky-50' },
              ].map((item) => (
                <div key={item.label} className={`${item.bg} rounded-2xl border border-background-200 p-4 text-center`}>
                  <i className={`${item.icono} text-foreground-500 text-lg mb-1 block`}></i>
                  <p className="text-foreground-950 font-bold text-lg">{item.valor}</p>
                  <p className="text-foreground-400 text-[10px]">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* ---- SALUDOS VIEW ---- */
          <div>
            {/* Ordenamiento */}
            <div className="flex items-center gap-1.5 mb-4 overflow-x-auto">
              {ordenamientos.map((ord) => (
                <button
                  key={ord.id}
                  onClick={() => setOrdenamiento(ord.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    ordenamiento === ord.id
                      ? 'bg-foreground-950 text-background-50'
                      : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-200'
                  }`}
                >
                  <i className={ord.icono}></i>
                  {ord.label}
                </button>
              ))}
            </div>

            {/* Filtros */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
              {/* Tipo */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Tipo:</span>
                {tipoPills.map((pill) => (
                  <button
                    key={pill.tipo}
                    onClick={() => setTipoFiltro(pill.tipo)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      tipoFiltro === pill.tipo
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <i className={`${pill.icono} text-[10px]`}></i>
                    {pill.label}
                  </button>
                ))}
              </div>

              {/* Destino */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Destino:</span>
                {destinoPills.map((pill) => (
                  <button
                    key={pill.destino}
                    onClick={() => setDestinoFiltro(pill.destino)}
                    className={`px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      destinoFiltro === pill.destino
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    {pill.label}
                  </button>
                ))}
              </div>

              {/* Estado */}
              <div className="flex items-center gap-1 flex-wrap">
                <span className="text-foreground-400 text-[10px] mr-1">Estado:</span>
                {estadoPills.map((pill) => (
                  <button
                    key={pill.estado}
                    onClick={() => setEstadoFiltro(pill.estado)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      estadoFiltro === pill.estado
                        ? 'bg-foreground-950 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-background-300'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${pill.dot}`}></span>
                    {pill.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Saludos Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ordenados.map((s) => {
                const tipoInfo = tipoLabels[s.tipo];
                const estadoInfo = estadoLabelsSaludo[s.estado];
                const destInfo = destinoLabels[s.destino];
                const isExpanded = saludoExpandido === s.id;

                return (
                  <div
                    key={s.id}
                    onClick={() => setSaludoExpandido(isExpanded ? null : s.id)}
                    className={`bg-background-50 rounded-2xl border p-5 hover:border-background-300 transition-colors cursor-pointer ${
                      s.estado === 'al_aire' ? 'border-emerald-200 bg-emerald-50/20' :
                      s.estado === 'destacado' ? 'border-amber-200 bg-amber-50/20' :
                      'border-background-200'
                    }`}
                  >
                    {/* Header: Tipo + Estado + Destino */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${tipoInfo.color} text-[9px] font-semibold`}>
                        <i className={`${tipoInfo.icono} text-[9px]`}></i>
                        {tipoInfo.label}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-[9px] text-foreground-500">
                          <i className={`${destInfo.icono} text-[9px]`}></i>
                          {destInfo.label}
                        </span>
                        <span className="flex items-center gap-1 text-[9px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${estadoInfo.dot}`}></span>
                          <span className="text-foreground-500">{estadoInfo.label}</span>
                        </span>
                      </div>
                    </div>

                    {/* Emoji */}
                    {s.tipo === 'emoji' && s.emoji && (
                      <div className="text-3xl mb-3 text-center">{s.emoji}</div>
                    )}

                    {/* Audio player */}
                    {s.tipo === 'audio' && s.audioDuracion && (
                      <div className="flex items-center gap-3 mb-3 bg-violet-50 rounded-xl px-4 py-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shrink-0">
                          <i className="ri-mic-fill text-background-50 text-base"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-end gap-0.5 mb-1">
                            {[...Array(7)].map((_, i) => (
                              <span
                                key={i}
                                className="w-1 bg-violet-400 rounded-full animate-pulse"
                                style={{ height: `${4 + Math.random() * 12}px`, animationDelay: `${i * 0.12}s` }}
                              ></span>
                            ))}
                          </div>
                          <p className="text-violet-500 text-[10px]">{s.audioDuracion}</p>
                        </div>
                      </div>
                    )}

                    {/* Imagen preview */}
                    {s.tipo === 'imagen' && s.imagenUrl && (
                      <div className="mb-3 rounded-xl overflow-hidden">
                        <img
                          src={s.imagenUrl}
                          alt="Saludo con imagen"
                          className="w-full h-40 object-cover"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Texto */}
                    <p className="text-foreground-600 text-xs leading-relaxed mb-3">
                      &ldquo;{s.texto}&rdquo;
                    </p>

                    {/* Remitente */}
                    <div className="flex items-center gap-2 mb-3">
                      <img
                        src={s.remitenteAvatar}
                        alt={s.remitente}
                        className="w-7 h-7 rounded-full object-cover"
                        loading="lazy"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground-600 text-[11px] font-medium">{s.remitente}</p>
                        <p className="text-foreground-400 text-[9px]">{s.remitenteCiudad}</p>
                      </div>
                    </div>

                    {/* Destino detail */}
                    <div className="flex items-center gap-1 mb-3">
                      <i className={`${destInfo.icono} text-foreground-400 text-[10px]`}></i>
                      <span className="text-foreground-500 text-[10px]">{s.destinoNombre}</span>
                      <span className="text-foreground-300 text-[9px]">&bull; {s.destinoRadio}</span>
                    </div>

                    {/* Timeline */}
                    <div className="flex items-center gap-3 text-[9px] text-foreground-400 mb-3">
                      <span>{new Date(s.fechaEnvio).toLocaleDateString('es-CR', { day: 'numeric', month: 'short' })} {new Date(s.fechaEnvio).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                      {s.fechaAlAire && (
                        <>
                          <span>&bull;</span>
                          <span className="text-emerald-600 flex items-center gap-1">
                            <span className="w-1 h-1 rounded-full bg-emerald-500"></span>
                            Al Aire {new Date(s.fechaAlAire).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </>
                      )}
                      {s.fechaDestacado && !s.fechaAlAire && (
                        <>
                          <span>&bull;</span>
                          <span className="text-amber-600 flex items-center gap-1">
                            <i className="ri-star-fill text-[9px]"></i>
                            Destacado
                          </span>
                        </>
                      )}
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-background-100 space-y-2">
                        {s.fechaLeido && (
                          <div className="flex items-center gap-2 text-[10px]">
                            <span className="text-foreground-400">Leído:</span>
                            <span className="text-foreground-600">{new Date(s.fechaLeido).toLocaleTimeString('es-CR', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        )}
                        {s.estado === 'recibido' && (
                          <div className="flex items-center gap-2 text-[10px] bg-background-100/80 rounded-lg px-3 py-2">
                            <i className="ri-time-line text-foreground-400"></i>
                            <span className="text-foreground-500">Tu saludo fue recibido. El locutor lo leerá pronto.</span>
                          </div>
                        )}
                        {s.estado === 'al_aire' && (
                          <div className="flex items-center gap-2 text-[10px] bg-emerald-50 rounded-lg px-3 py-2">
                            <i className="ri-broadcast-line text-emerald-600"></i>
                            <span className="text-emerald-700">¡Tu saludo salió al aire! La comunidad lo escuchó en vivo.</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Stats Footer */}
                    <div className="flex items-center gap-4 pt-2 border-t border-background-100">
                      <span className="text-foreground-400 text-[10px] flex items-center gap-1">
                        <i className="ri-share-forward-line"></i>{s.compartidos}
                      </span>
                      <span className="text-emerald-700 text-[10px] font-semibold ml-auto flex items-center gap-0.5">
                        <i className="ri-flashlight-fill"></i>{s.score} pts
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {ordenados.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-message-3-line text-foreground-400 text-2xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">No hay saludos que mostrar con estos filtros.</p>
                <button
                  onClick={() => { setTipoFiltro('todos'); setDestinoFiltro('todos'); setEstadoFiltro('todos'); setOrdenamiento('recientes'); }}
                  className="mt-3 px-4 py-2 rounded-lg bg-background-100 text-foreground-600 text-xs font-semibold cursor-pointer hover:bg-background-200 transition-colors"
                >
                  Limpiar Filtros
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 pb-12">
        <div className="bg-gradient-to-r from-rose-500 to-amber-500 rounded-3xl p-8 md:p-10 text-center">
          <h3 className="text-white font-bold text-lg md:text-xl mb-2">¿Querés Enviar un Saludo?</h3>
          <p className="text-white/80 text-sm max-w-[500px] mx-auto mb-6 leading-relaxed">
            Entrá a la Cabina de tu radio favorita y enviá tu saludo con texto, audio, emoji o imagen. Sumá score, conectá con la comunidad y hacé que tu saludo llegue al aire.
          </p>
          <Link
            to="/cabina-2"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-background-50 text-foreground-950 font-bold text-sm cursor-pointer hover:bg-background-100 transition-all whitespace-nowrap"
          >
            <i className="ri-broadcast-line"></i>
            Ir a la Cabina
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}